import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, StoreIcon, TagIcon, CalendarIcon } from 'lucide-react';
import { Store, Brand, BrandRequest } from '../../data/types';
interface SearchBarProps {
  stores?: Store[];
  brands?: Brand[];
  brandRequests?: BrandRequest[];
  onSelectStore?: (store: Store) => void;
  onSelectBrand?: (brand: Brand) => void;
}
export function SearchBar({
  stores = [],
  brands = [],
  brandRequests = [],
  onSelectStore,
  onSelectBrand
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const filteredStores = stores.filter(s => s.name.toLowerCase().includes(query.toLowerCase()) || s.location.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  const filteredBrands = brands.filter(b => b.name.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  const filteredCampaigns = brandRequests.filter(r => r.campaignName?.toLowerCase().includes(query.toLowerCase()) || r.brandName.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  const hasResults = filteredStores.length > 0 || filteredBrands.length > 0 || filteredCampaigns.length > 0;
  return <div ref={wrapperRef} className="relative w-full max-w-md hidden md:block" data-id="element-966">
      <div className="relative" data-id="element-967">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-968">
          <SearchIcon className="h-4 w-4 text-text-tertiary" data-id="element-969" />
        </div>
        <input type="text" className="block w-full pl-10 pr-3 py-2 border border-border rounded-md leading-5 bg-surface-warm placeholder-text-tertiary focus:outline-none focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition-colors" placeholder="Search stores, brands, campaigns..." value={query} onChange={e => {
        setQuery(e.target.value);
        setIsOpen(e.target.value.length > 0);
      }} onFocus={() => {
        if (query.length > 0) setIsOpen(true);
      }} data-id="element-970" />
      </div>

      {isOpen && query.length > 0 && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" data-id="element-971">
          {!hasResults ? <div className="px-4 py-3 text-sm text-text-secondary text-center" data-id="element-972">
              No results found for "{query}"
            </div> : <>
              {filteredStores.length > 0 && <div className="py-1" data-id="element-973">
                  <div className="px-3 py-1 text-xs font-semibold text-text-tertiary uppercase tracking-wider" data-id="element-974">
                    Stores
                  </div>
                  {filteredStores.map(store => <button key={store.id} className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface hover:text-primary flex items-center gap-2" onClick={() => {
            onSelectStore?.(store);
            setIsOpen(false);
            setQuery('');
          }} data-id="element-975">
                      <StoreIcon className="w-4 h-4 text-text-tertiary" data-id="element-976" />
                      <span data-id="element-977">{store.name}</span>
                      <span className="text-text-tertiary text-xs ml-auto" data-id="element-978">
                        {store.location}
                      </span>
                    </button>)}
                </div>}

              {filteredBrands.length > 0 && <div className="py-1 border-t border-border" data-id="element-979">
                  <div className="px-3 py-1 text-xs font-semibold text-text-tertiary uppercase tracking-wider" data-id="element-980">
                    Brands
                  </div>
                  {filteredBrands.map(brand => <button key={brand.id} className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface hover:text-primary flex items-center gap-2" onClick={() => {
            onSelectBrand?.(brand);
            setIsOpen(false);
            setQuery('');
          }} data-id="element-981">
                      <TagIcon className="w-4 h-4 text-text-tertiary" data-id="element-982" />
                      <span data-id="element-983">{brand.name}</span>
                    </button>)}
                </div>}

              {filteredCampaigns.length > 0 && <div className="py-1 border-t border-border" data-id="element-984">
                  <div className="px-3 py-1 text-xs font-semibold text-text-tertiary uppercase tracking-wider" data-id="element-985">
                    Campaigns
                  </div>
                  {filteredCampaigns.map(campaign => <button key={campaign.id} className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface hover:text-primary flex items-center gap-2" onClick={() => {
            // Handle campaign click (could navigate to calendar or requests)
            setIsOpen(false);
            setQuery('');
          }} data-id="element-986">
                      <CalendarIcon className="w-4 h-4 text-text-tertiary" data-id="element-987" />
                      <div className="flex flex-col" data-id="element-988">
                        <span data-id="element-989">
                          {campaign.campaignName || 'Unnamed Campaign'}
                        </span>
                        <span className="text-text-tertiary text-xs" data-id="element-990">
                          {campaign.brandName} · Week {campaign.weekNumber}
                        </span>
                      </div>
                    </button>)}
                </div>}
            </>}
        </div>}
    </div>;
}